import { ReactNode, createContext } from "react";
import useContrato, { ContratoAssinaturaType, ContratoType, CreateContratoType, FlashCnpjType, UpdateContratoType } from "./hooks/useContrato";
import { DeleteGuardResult } from "../types/deleteGuard";

interface ContratoContextProviderProps {
    children: ReactNode;
}

export interface ContratoContextData {
    getAllContratoPaged: (
        pageNumber: number,
        pageSize: number,
        description?: string,
        empresaId?: number,
        status?: number,
        city?: string,
        startDate?: string,
        endDate?: string,
        idContrato?: number,
        state?: string,
        region?: string,
        isPrivate?: boolean,
        vendedorId?: number,
        somenteKit?: boolean,
    ) => Promise<ContratoType[] | null>;
    setContrato: any;
    contrato: ContratoType;
    setListContrato: any;
    listContrato: ContratoType[];
    setInitialListContrato: any;
    initialListContrato: ContratoType[];
    setPageCount: any;
    pageCount: number;
    createContrato: (createContratoData: CreateContratoType) => Promise<CreateContratoType | null>;
    updateContrato: (updateContratoData: UpdateContratoType) => Promise<UpdateContratoType | null>;
    deleteContrato: (id: number, force?: boolean) => Promise<DeleteGuardResult>;
    downloadContratoPdf: (contratoId: number, fallbackBase64?: string) => void;
    getContratoCursoGuidByCursoIdAndContratoId: (cursoId: number, contratoId: number) => Promise<string | null>;
    updateStatusContrato: (empresaId: number, status: number) => Promise<boolean>;
    handleGetContratoByEmpresaId: (empresaId: number) => Promise<ContratoType[] | null>;
    getContratoById: (id: number) => Promise<ContratoType | null>;
    suggestContratoDescriptions: (input: string, limit?: number) => Promise<string[]>;
    replaceCursosDoContrato: (contratoId: number, cursoIds: number[]) => Promise<boolean>;
    addCursoAoContrato: (contratoId: number, cursoId: number) => Promise<boolean>;
    removeCursoDoContrato: (contratoId: number, cursoId: number, force?: boolean) => Promise<DeleteGuardResult>;
    enviarParaAssinatura: (contratoId: number, cnpj: string) => Promise<ContratoAssinaturaType | null>;
    getStatusAssinatura: (contratoId: number) => Promise<ContratoAssinaturaType | null>;
    getFlashCnpjs: () => Promise<FlashCnpjType[]>;
}

const initialState: ContratoContextData = {
    getAllContratoPaged: async (
        pageNumber: number,
        pageSize: number,
        description?: string,
        officeId?: number,
        status?: number,
        city?: string,
        startDate?: string,
        endDate?: string,
        idContrato?: number,
        state?: string,
        region?: string,
        isPrivate?: boolean,
        vendedorId?: number,
        somenteKit?: boolean,
    ) => {
        return Promise.resolve(null);
    },
    setContrato: () => { },
    contrato: {
        id: 0,
        description: "",
        contractAnnex: "",
        class: "",
        hiredStudents: 0,
        currentStudents: 0,
        completionDate: '',
        dateOfDischarge: '',
        linkSent: false,
        freebies: false,
        empresaId: 0,
        instituicaoId: 0,
        cursoId: [],
        vendedorId: 0,
        responsavelId: 0,
        alunoId: [],
        agendaId: [],
        status: 0,
    },
    setListContrato: () => { },
    listContrato: [],
    setInitialListContrato: () => { },
    initialListContrato: [],
    setPageCount: () => { },
    pageCount: 0,
    createContrato: async (createContratoData: CreateContratoType) => {
        return Promise.resolve(null);
    },
    updateContrato: async (updateContratoData: UpdateContratoType) => {
        return Promise.resolve(null);
    },
    deleteContrato: async () => {
        return Promise.resolve({ status: "error" } as DeleteGuardResult);
    },
    downloadContratoPdf: () => { },
    getContratoCursoGuidByCursoIdAndContratoId: async (cursoId: number, contratoId: number) => {
        return Promise.resolve(null);
    },
    updateStatusContrato: async (empresaId: number, status: number) => {
        return Promise.resolve(false);
    },
    handleGetContratoByEmpresaId: async (empresaId: number) => {
        return Promise.resolve(null);
    },
    getContratoById: async (id: number) => {
        return Promise.resolve(null);
    },
    suggestContratoDescriptions: async (input: string, limit?: number) => {
        return Promise.resolve([]);
    },
    replaceCursosDoContrato: async () => Promise.resolve(false),
    addCursoAoContrato: async () => Promise.resolve(false),
    removeCursoDoContrato: async () => Promise.resolve({ status: "error" } as DeleteGuardResult),
    enviarParaAssinatura: async () => Promise.resolve(null),
    getStatusAssinatura: async () => Promise.resolve(null),
    getFlashCnpjs: async () => Promise.resolve([]),
};

export const ContratoContext =
    createContext<ContratoContextData>(initialState);

export default function ContratoContextProvider({
    children,
}: ContratoContextProviderProps) {
    const {
        getAllContratoPaged,
        createContrato,
        updateContrato,
        deleteContrato,
        contrato,
        setContrato,
        setListContrato,
        listContrato,
        initialListContrato,
        setInitialListContrato,
        setPageCount,
        pageCount,
        downloadContratoPdf,
        getContratoCursoGuidByCursoIdAndContratoId,
        updateStatusContrato,
        handleGetContratoByEmpresaId,
        getContratoById,
        suggestContratoDescriptions,
        replaceCursosDoContrato,
        addCursoAoContrato,
        removeCursoDoContrato,
        enviarParaAssinatura,
        getStatusAssinatura,
        getFlashCnpjs,
    } = useContrato();

    return (
        <ContratoContext.Provider
            value={{
                getAllContratoPaged,
                createContrato,
                updateContrato,
                deleteContrato,
                contrato,
                setContrato,
                setListContrato,
                listContrato,
                initialListContrato,
                setInitialListContrato,
                setPageCount,
                pageCount,
                downloadContratoPdf,
                getContratoCursoGuidByCursoIdAndContratoId,
                updateStatusContrato,
                handleGetContratoByEmpresaId,
                getContratoById,
                suggestContratoDescriptions,
                replaceCursosDoContrato,
                addCursoAoContrato,
                removeCursoDoContrato,
                enviarParaAssinatura,
                getStatusAssinatura,
                getFlashCnpjs,
            }}
        >
            {children}
        </ContratoContext.Provider>
    );
}